import { Platform } from '@angular/cdk/platform';
import { inject, Injectable } from '@angular/core';
import { complianceLogoByModuleId } from '@modules/forms/form-modules/woundtrack/modules/compliance-logo/compliance-logo-by-module-id.config';
import { StudioExportService } from '@modules/studio/studio/studio-export.service';
import { ModuleId } from '@pixacare/forms';
import * as htmlToImage from 'html-to-image';
import { firstValueFrom, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import colors from 'tailwindcss/colors';

@Injectable()
export class WoundtrackExportService {
  private readonly resultPaneWidthRatio = 30 / 100;
  private readonly resultPaneFillColor = colors.slate['50'];
  private readonly logoHeightRatio = 1.5 / 100;
  private readonly logoPaddingRatio = 1 / 100;

  private readonly exportService = inject(StudioExportService);
  private readonly platform = inject(Platform);

  getExportPicture(targetModuleId?: ModuleId): Observable<string> {
    const logoPath = targetModuleId ? complianceLogoByModuleId[targetModuleId] : null;
    return forkJoin({
      studioImage: this.getStudioPicture(),
      complianceLogo: logoPath ? this.loadImage(logoPath) : of(null),
    }).pipe(
      switchMap(({ studioImage, complianceLogo }) =>
        this.getResultsPicture(studioImage.width).pipe(
          map((resultsImage) => ({ studioImage, resultsImage, complianceLogo })),
        ),
      ),
      map(({ studioImage, resultsImage, complianceLogo }) => {
        const canvas = document.createElement('canvas');
        canvas.width = studioImage.width + resultsImage.width;
        canvas.height = Math.max(studioImage.height, resultsImage.height);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        ctx.fillStyle = this.resultPaneFillColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(studioImage, 0, 0);
        ctx.drawImage(resultsImage, studioImage.width, 0);

        if (complianceLogo) {
          const complianceLogoHeight = canvas.height * this.logoHeightRatio;
          const complianceLogoWidth =
            complianceLogo.naturalWidth * (complianceLogoHeight / complianceLogo.naturalHeight);
          const complianceLogoPadding = canvas.height * this.logoPaddingRatio;
          ctx.drawImage(
            complianceLogo,
            canvas.width - complianceLogoWidth - complianceLogoPadding,
            canvas.height - complianceLogoHeight - complianceLogoPadding,
            complianceLogoWidth,
            complianceLogoHeight,
          );
        }

        return canvas.toDataURL('image/jpeg', 1);
      }),
    );
  }

  private getStudioPicture(): Observable<HTMLImageElement> {
    return this.loadImage(this.exportService.getResult().base64);
  }

  private getResultsPicture(targetWidth: number): Observable<HTMLImageElement> {
    const htmlElement = document.getElementById('woundtrackResults');
    if (!htmlElement) {
      throw new Error('Element with id "woundtrackResults" not found');
    }
    const { width, height } = htmlElement.getBoundingClientRect();
    const canvasWidth = targetWidth * this.resultPaneWidthRatio;
    const canvasHeight = height * (canvasWidth / width);
    return from(this.renderResults(htmlElement, canvasWidth, canvasHeight)).pipe(
      map((canvas: HTMLCanvasElement) => canvas.toDataURL()),
      switchMap((dataUrl: string) => this.loadImage(dataUrl)),
    );
  }

  // WebKit draws the box-shadows of an SVG image onto a canvas mirrored and out of scale: the card shadows of the results
  // come out as hard bars. A bitmap it rasterizes itself keeps them. Other engines taint such a bitmap, so they draw the
  // image as usual.
  private async renderResults(
    htmlElement: HTMLElement,
    canvasWidth: number,
    canvasHeight: number,
  ): Promise<HTMLCanvasElement> {
    if (!this.platform.WEBKIT) {
      return htmlToImage.toCanvas(htmlElement, { canvasWidth, canvasHeight });
    }

    const svgImage = await firstValueFrom(this.loadImage(await htmlToImage.toSvg(htmlElement)));
    const bitmap = await createImageBitmap(svgImage, {
      resizeWidth: Math.round(canvasWidth * window.devicePixelRatio),
      resizeHeight: Math.round(canvasHeight * window.devicePixelRatio),
    });
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    ctx.drawImage(bitmap, 0, 0);
    return canvas;
  }

  private loadImage(src: string): Observable<HTMLImageElement> {
    return new Observable((observer): void => {
      const img = new Image();
      img.onload = (): void => {
        observer.next(img);
        observer.complete();
      };
      img.onerror = (err): void => observer.error(err);
      img.src = src;
    });
  }
}
